import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  createContext,
  useContext,
} from "react";
import { useLocation, useNavigate, Outlet, NavLink } from "react-router-dom";
import Map from "../components/map/Map";
import { useSelector } from "react-redux";
import { Drawer, Button } from "antd";
import ws from "../utils/ws";
import Header from "../components/header/Header";
import closeIcon from "../assets/images/close-icon.svg";
import { ReactComponent as SupportIcon } from "../assets/images/support-icon.svg";
import { useAuth } from "../AuthProvider";
import { clearAuthTokens } from "../utils/operatorAuth";

const HomeContext = createContext();

export const useHome = () => useContext(HomeContext);

const FIRST_LEVEL_MOBILE_DRAWER_PATHS = new Set([
  "/",
  "/search",
  "/transports",
  "/companies",
  "/my-orders",
  "/my-fleet",
  "/notifications",
]);

const normalizePathname = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;

const isFirstLevelMobileDrawerPath = (pathname) =>
  FIRST_LEVEL_MOBILE_DRAWER_PATHS.has(normalizePathname(pathname));

const getMobileRouteDrawerTitle = (pathname) => {
  switch (normalizePathname(pathname)) {
    case "/companies":
      return "Мои компании";
    case "/":
    case "/search":
    case "/transports":
      return "Я ищу";
    default:
      return null;
  }
};

const Home = () => {
  const wsConnectionRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setCompanyProfile,
    setShowProfile,
    showProfile,
    companyProfile,
    companyProfileData,
  } = useAuth();

  const handleLogout = () => {
    clearAuthTokens();
    window.location.href = "/login";
  };

  // Drivers work from the fleet cabinet (ТЗ §8/§10): the order-search home is
  // a dispatcher tool, so land drivers on «Мой транспорт» instead.
  useEffect(() => {
    if (user?.role === "DRIVER" && location.pathname === "/") {
      navigate("/my-fleet", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const [drivers, setDrivers] = useState([]);
  const [newOffer, setNewOffer] = useState(null);
  const [offerAccepted, setOfferAccepted] = useState(null);
  const [deliverStatusChanged, setDeliverStatusChanged] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [scrollTo, setScrollTo] = useState(null);
  const [showOnMap, setShowOnMap] = useState(null);
  const [selectedSearch, setSelectedSearch] = useState("orders");
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [show, setShow] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [middleSidebarExpanded, setMiddleSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileRouteDrawerOpen, setMobileRouteDrawerOpen] = useState(
    window.innerWidth <= 992 && isFirstLevelMobileDrawerPath(location.pathname),
  );
  const [mobileRouteDrawerConfig, setMobileRouteDrawerConfig] = useState({});
  const [mobileCreateDrawerOpen, setMobileCreateDrawerOpen] = useState(false);
  const [mobileSupportDrawerOpen, setMobileSupportDrawerOpen] = useState(false);
  const profileModalOpen = showProfile || companyProfile;
  const wasProfileModalOpenRef = useRef(profileModalOpen);
  const isFirstLevelMobileRoute = isFirstLevelMobileDrawerPath(location.pathname);
  const shouldUseMobileRouteDrawer = isMobile && isFirstLevelMobileRoute;
  const normalizedPathname = normalizePathname(location.pathname);
  const isMobileOtherLevelDrawer =
    mobileRouteDrawerConfig.level === "other" || normalizedPathname === "/companies";
  const mobileRouteDrawerTitle =
    mobileRouteDrawerConfig.title ?? getMobileRouteDrawerTitle(location.pathname);
  const showMobileRouteDrawerHeader =
    mobileRouteDrawerConfig.showHeader ?? Boolean(mobileRouteDrawerTitle);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useLayoutEffect(() => {
    if (!isMobile) {
      wasProfileModalOpenRef.current = profileModalOpen;
      return;
    }

    if (profileModalOpen) {
      setMobileDrawerOpen(false);
    } else if (wasProfileModalOpenRef.current) {
      setMobileRouteDrawerOpen(false);
      setMobileCreateDrawerOpen(false);
      setMobileSupportDrawerOpen(false);
      setMobileDrawerOpen(true);
    }

    wasProfileModalOpenRef.current = profileModalOpen;
  }, [isMobile, profileModalOpen]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const panel = searchParams.get("panel");

    if (panel === "profile") {
      setCompanyProfile(false);
      setShowProfile(true);
    } else if (panel === "company-profile") {
      setShowProfile(false);
      setCompanyProfile(true);
    } else {
      return;
    }

    searchParams.delete("panel");
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, setCompanyProfile, setShowProfile]);

  useEffect(() => {
    setShowOnMap(null);
  }, [location.pathname, selectedSearch]);

  useEffect(() => {
    setMobileDrawerOpen(false);
    setMobileSupportDrawerOpen(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (isMobile && isFirstLevelMobileRoute) {
      setMobileDrawerOpen(false);
      setMobileCreateDrawerOpen(false);
      setMobileSupportDrawerOpen(false);
      setMobileRouteDrawerOpen(true);
    } else {
      setMobileRouteDrawerOpen(false);
    }

    if (!isMobile || !isFirstLevelMobileRoute) {
      setMobileRouteDrawerConfig({});
    }
  }, [isMobile, isFirstLevelMobileRoute, location.pathname]);

  useEffect(() => {
    const body = document.querySelector("body");
    if (sidebarExpanded) {
      body.classList.add("bd-sidebar-expanded-small");
    } else {
      body.classList.remove("bd-sidebar-expanded");
    }
  }, [sidebarExpanded]);

  useEffect(() => {
    if (newOffer || offerAccepted || deliverStatusChanged) {
      setShowNotification(true);
    }
  }, [newOffer, offerAccepted, deliverStatusChanged]);

  // Use the pathname to determine when to show the sidebar
  useEffect(() => {
    const path = location.pathname;

    // Show sidebar for all routes except root with no path
    if (path !== "/") {
      setShow(true);
    }

    setMiddleSidebarExpanded(false);
  }, [location.pathname]); // Re-run when URL path changes

  useEffect(() => {
    if (!wsConnectionRef.current) {
      wsConnectionRef.current = new ws({
        user,
        token: window.localStorage.getItem("access"),
        setDrivers,
        setNewOffer,
        setOfferAccepted,
        setDeliverStatusChanged,
      });

      wsConnectionRef.current.connect();
      window.wsConnection = wsConnectionRef.current;
    }
    return () => {
      if (wsConnectionRef.current) {
        wsConnectionRef.current.disconnect();
        wsConnectionRef.current = null;
      }
    };
  }, [user]);

  const toggleSidebar = (path) => {
    const currentPath = location.pathname;

    if (show && currentPath === path) {
      // If we're on the same path and sidebar is shown, go home and hide sidebar
      navigate("/");
      setShow(false);
    } else {
      // Navigate to the new path and ensure sidebar is shown
      navigate(path);
      setShow(true);
    }
  };

  const closeMiddleSidebar = () => {
    setMiddleSidebarExpanded(false);
    navigate("/");
  };

  const openMobileCreateDrawer = () => {
    setMobileDrawerOpen(false);
    setMobileRouteDrawerOpen(false);
    setMobileSupportDrawerOpen(false);
    window.dispatchEvent(new CustomEvent('closeMobileDrawers'));
    setMobileCreateDrawerOpen(true);
  };

  const openMobileSupportDrawer = () => {
    setMobileDrawerOpen(false);
    setMobileRouteDrawerOpen(false);
    setMobileCreateDrawerOpen(false);
    window.dispatchEvent(new CustomEvent('closeMobileDrawers'));
    setMobileSupportDrawerOpen(true);
  };

  const closeMobileSupportDrawer = () => {
    setMobileSupportDrawerOpen(false);
    setMobileRouteDrawerOpen(false);
    setMobileCreateDrawerOpen(false);
    setMobileDrawerOpen(true);
  };

  const closeMobileRouteDrawer = () => {
    setMobileRouteDrawerOpen(false);
    setMobileCreateDrawerOpen(false);
    setMobileSupportDrawerOpen(false);

    if (isMobile && normalizedPathname === "/companies") {
      setMobileDrawerOpen(true);
    }
  };

  const openCurrentMobileRouteDrawer = () => {
    setMobileRouteDrawerOpen(true);
  };

  const handleMobileRouteClick = (path, { search = false, companies = false } = {}) => {
    if (!isMobile) {
      return;
    }

    setMobileDrawerOpen(false);
    setMobileCreateDrawerOpen(false);
    setMobileSupportDrawerOpen(false);

    if (search || companies || isFirstLevelMobileDrawerPath(path)) {
      setMobileRouteDrawerOpen(true);
      return;
    }

    setMobileRouteDrawerOpen(false);

    if (location.pathname === path) {
      openCurrentMobileRouteDrawer();
    }
  };

  const handleMobileCreate = (path) => {
    setMobileCreateDrawerOpen(false);
    if (companyProfileData) {
      navigate(path);
    } else {
      setCompanyProfile(true);
    }
  };

  const mailIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 6H20C20.5523 6 21 6.44772 21 7V17C21 17.5523 20.5523 18 20 18H4C3.44772 18 3 17.5523 3 17V7C3 6.44772 3.44772 6 4 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 8L12 13L20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const phoneIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 4H5.5C4.67157 4 4 4.67157 4 5.5C4 13.5081 10.4919 20 18.5 20C19.3284 20 20 19.3284 20 18.5V16.5C20 16.2239 19.7761 16 19.5 16H15.6C15.3715 16 15.172 16.1552 15.1161 16.3768L14.6866 18.0948C11.2234 17.2519 8.74808 14.7766 7.90521 11.3134L9.62318 10.8839C9.84478 10.828 10 10.6285 10 10.4V6.5C10 6.22386 9.77614 6 9.5 6H7.5V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const whatsappIcon = (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.9997 1.83337C5.93713 1.83337 1.83301 5.93749 1.83301 11C1.83301 12.6656 2.27734 14.2275 3.0537 15.5734L1.83301 20.1667L6.59509 19.041C7.90236 19.7585 9.40315 20.1667 10.9997 20.1667C16.0622 20.1667 20.1663 16.0626 20.1663 11C20.1663 5.93749 16.0622 1.83337 10.9997 1.83337ZM10.9997 18.5249C9.46757 18.5249 8.04266 18.067 6.8538 17.2805L4.04039 17.997L4.82949 15.3079C3.97572 14.0874 3.47486 12.6022 3.47486 10.9998C3.47486 6.84398 6.84386 3.47497 10.9997 3.47497C15.1555 3.47497 18.5245 6.84398 18.5245 10.9998C18.5245 15.1556 15.1555 18.5246 10.9997 18.5246V18.5249Z" fill="#25D366" />
      <path d="M13.1775 12.223L15.423 13.2816C15.5261 13.3301 15.5921 13.4347 15.5828 13.5482C15.5586 13.8436 15.44 14.4358 14.9091 14.9667C13.4108 16.4651 10.7201 14.7697 10.6105 14.7042C9.94873 14.3487 9.31995 13.873 8.72364 13.277C8.1276 12.6809 7.65169 12.0518 7.29618 11.39C7.23044 11.2807 5.53512 8.58985 7.03373 7.09117C7.56455 6.56032 8.15673 6.44173 8.45218 6.41749C8.56587 6.40821 8.67028 6.47421 8.71875 6.57734L9.77755 8.82292C9.82756 8.92914 9.80565 9.05547 9.72264 9.13849L8.9335 9.92766C8.76283 10.0983 8.71282 10.3641 8.83038 10.575C9.11834 11.0914 9.50557 11.5888 9.95414 12.0464C10.4117 12.495 10.9091 12.8825 11.4254 13.1702C11.6363 13.2878 11.9019 13.2378 12.0728 13.0671L12.8619 12.2779C12.9449 12.1949 13.0713 12.1727 13.1775 12.223Z" fill="#25D366" />
    </svg>
  );

  const supportContacts = [
    {
      key: "email",
      href: "mailto:info@itruck.io",
      label: "info@itruck.io",
      mobileIcon: mailIcon,
    },
    {
      key: "phone",
      href: "tel:+996709222111",
      label: "+996 709 222 111",
      mobileIcon: phoneIcon,
    },
    {
      key: "whatsapp",
      href: "https://wa.me/996709222111",
      label: "WhatsApp",
      external: true,
      desktopIcon: whatsappIcon,
      mobileIcon: whatsappIcon,
      modifierClassName: "support-menu-link--whatsapp",
    },
  ];

  const renderMobileSupportLink = (item) => (
    <a
      key={item.key}
      href={item.href}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noopener noreferrer" : undefined}
      className="mobile-drawer-menu__item mobile-support-drawer__item"
    >
      <span className="mobile-drawer-menu__icon">
        {item.mobileIcon}
      </span>
      <span className="mobile-drawer-menu__title">
        {item.label}
      </span>
    </a>
  );

  const outletContent = (
    <Outlet
      context={{
        drivers,
        setScrollTo,
        selectedSearch,
        setSelectedSearch,
        setShowOnMap,
        selectedTransport,
        setSelectedTransport,
        selectedOrder,
        setSelectedOrder: (order) => {
          setSelectedOrder(order);
          if (order) {
            navigate(`/selected-order/${order}`);
          }
        },
        setShow,
      }}
    />
  );

  const mobileSupportContent = supportContacts.map(renderMobileSupportLink);

  return (
    <HomeContext.Provider
      value={{
        drivers,
        toggleSidebar,
        setSelectedTransport,
        setSelectedOrder,
        setScrollTo,
        setShowOnMap,
        newOffer,
        offerAccepted,
        setNewOffer,
        setOfferAccepted,
        setShowNotification,
        deliverStatusChanged,
        setDeliverStatusChanged,
        mobileDrawerOpen,
        setMobileDrawerOpen,
        mobileRouteDrawerOpen,
        setMobileRouteDrawerOpen,
        setMobileRouteDrawerConfig,
      }}
    >
      <div className={`page home ${location.pathname === "/companies" ? "pages-companies" : ""}`}>
        <Header />

        <div className="mobile-create-fab">
          <Button
            type="primary"
            size="large"
            onClick={openMobileCreateDrawer}
            style={{ boxShadow: '0 4px 10px rgba(47, 128, 237, 0.4)' }}
          >
            Создать
          </Button>
        </div>

        <Drawer
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ width: '24px' }}></span>
              <span style={{ fontWeight: '600' }}>Меню</span>
              <img
                src={closeIcon}
                alt="close"
                onClick={() => setMobileDrawerOpen(false)}
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              />
            </div>
          }
          placement="bottom"
          closable={false}
          onClose={() => setMobileDrawerOpen(false)}
          open={mobileDrawerOpen}
          height="auto"
          push={false}
          className="mobile-only"
          rootClassName="mobile-drawer-menu-container"
        >
          <div className="mobile-drawer-menu" onClick={() => setMobileDrawerOpen(false)}>
            <div
              className="mobile-drawer-menu__item"
              onClick={(e) => {
                e.stopPropagation();
                setCompanyProfile(false);
                setShowProfile(true);
              }}
            >
              <div className="mobile-drawer-menu__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 14.666C14.8497 14.666 17.1341 15.5752 18.6885 16.7734C20.2793 17.9998 21 19.4318 21 20.4395C21 20.6153 20.945 20.7028 20.8418 20.7783C20.7022 20.8805 20.402 21 19.8701 21H4.11816C3.58609 21 3.29063 20.8801 3.15527 20.7803C3.05654 20.7074 3.00001 20.6206 3 20.4395C3 19.4308 3.71903 17.9991 5.30762 16.7734C6.86013 15.5757 9.144 14.666 12 14.666ZM8.23047 6.79883C8.22131 4.66677 9.97155 3 12 3C14.0321 3 15.7694 4.63814 15.7695 6.78125C15.7695 9.02404 14.0226 10.707 12 10.707C9.98477 10.707 8.23056 9.01969 8.23047 6.80371V6.79883Z" stroke="#333333" strokeWidth="2" />
                </svg>
              </div>
              <div className="mobile-drawer-menu__title">Мой Профиль</div>
            </div>

            <div
              className="mobile-drawer-menu__item"
              onClick={(e) => {
                e.stopPropagation();
                setShowProfile(false);
                setCompanyProfile(true);
              }}
            >
              <div className="mobile-drawer-menu__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 22H4C3.4 22 3 21.6 3 21V7.60004C3 6.20004 4 5.00004 5.3 4.70004L12.7 3.00005C13 2.90005 13.3 3.00004 13.5 3.20004C13.7 3.40004 13.9 3.70005 13.9 4.00005V21C14 21.5 13.6 22 13 22ZM5 20H12V5.20004L5.8 6.60004C5.3 6.70004 5 7.10004 5 7.60004V20Z" fill="currentColor" />
                  <path d="M21 22H13C12.4 22 12 21.6 12 21V6.00005C12 5.70005 12.1 5.40004 12.4 5.20004C12.6 5.00004 13 4.90005 13.3 5.00005L19.8 6.60004C21.1 6.90004 22.1 8.10005 22.1 9.50005V20.9C22 21.5 21.6 22 21 22ZM14 20H20V9.60004C20 9.10004 19.7 8.70004 19.2 8.60004L14 7.30005V20Z" fill="currentColor" />
                  <path d="M18 11H16C15.4 11 15 10.6 15 10C15 9.4 15.4 9 16 9H18C18.6 9 19 9.4 19 10C19 10.6 18.6 11 18 11Z" fill="currentColor" />
                  <path d="M17.9996 14H16.0996C15.4996 14 15.0996 13.6 15.0996 13C15.0996 12.4 15.4996 12 16.0996 12H17.9996C18.5996 12 18.9996 12.4 18.9996 13C18.9996 13.6 18.5996 14 17.9996 14Z" fill="currentColor" />
                  <path d="M18 17H16C15.4 17 15 16.6 15 15 15.4 15 16 15H18C18.6 15 19 15.4 19 16C19 16.6 18.6 17 18 17Z" fill="currentColor" />
                  <path d="M9 22H8C7.4 22 7 21.6 7 21V18C7 17.4 7.4 17 8 17H9C9.6 17 10 17.4 10 18V21C10 21.5 9.6 22 9 22Z" fill="currentColor" />
                </svg>
              </div>
              <div className="mobile-drawer-menu__title">Профиль компании</div>
            </div>

            <NavLink
              to="/companies"
              className={({ isActive }) => `mobile-drawer-menu__item ${isActive ? "active" : ""}`}
              onClick={(e) => {
                setMobileDrawerOpen(false);
                setMobileRouteDrawerOpen(true);
                setMobileCreateDrawerOpen(false);
                if (location.pathname === "/companies") {
                  e.preventDefault();
                }
              }}
            >
              <div className="mobile-drawer-menu__icon mobile-drawer-menu__icon--companies">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 22H4C3.4 22 3 21.6 3 21V7.60004C3 6.20004 4 5.00004 5.3 4.70004L12.7 3.00005C13 2.90005 13.3 3.00004 13.5 3.20004C13.7 3.40004 13.9 3.70005 13.9 4.00005V21C14 21.5 13.6 22 13 22ZM5 20H12V5.20004L5.8 6.60004C5.3 6.70004 5 7.10004 5 7.60004V20Z" fill="currentColor" />
                  <path d="M21 22H13C12.4 22 12 21.6 12 21V6.00005C12 5.70005 12.1 5.40004 12.4 5.20004C12.6 5.00004 13 4.90005 13.3 5.00005L19.8 6.60004C21.1 6.90004 22.1 8.10005 22.1 9.50005V20.9C22 21.5 21.6 22 21 22ZM14 20H20V9.60004C20 9.10004 19.7 8.70004 19.2 8.60004L14 7.30005V20Z" fill="currentColor" />
                  <path d="M18 11H16C15.4 11 15 10.6 15 10C15 9.4 15.4 9 16 9H18C18.6 9 19 9.4 19 10C19 10.6 18.6 11 18 11Z" fill="currentColor" />
                  <path d="M17.9996 14H16.0996C15.4996 14 15.0996 13.6 15.0996 13C15.0996 12.4 15.4996 12 16.0996 12H17.9996C18.5996 12 18.9996 12.4 18.9996 13C18.9996 13.6 18.5996 14 17.9996 14Z" fill="currentColor" />
                  <path d="M18 17H16C15.4 17 15 16.6 15 15 15.4 15 16 15H18C18.6 15 19 15.4 19 16C19 16.6 18.6 17 18 17Z" fill="currentColor" />
                  <path d="M9 22H8C7.4 22 7 21.6 7 21V18C7 17.4 7.4 17 8 17H9C9.6 17 10 17.4 10 18V21C10 21.5 9.6 22 9 22Z" fill="currentColor" />
                </svg>
              </div>
              <div className="mobile-drawer-menu__title">Все компании</div>
            </NavLink>

            <button
              type="button"
              className="mobile-drawer-menu__item mobile-drawer-menu__item--button"
              onClick={(e) => {
                e.stopPropagation();
                openMobileSupportDrawer();
              }}
            >
              <div className="mobile-drawer-menu__icon">
                <SupportIcon />
              </div>
              <div className="mobile-drawer-menu__title">Служба поддержки</div>
            </button>

            <div className="mobile-drawer-menu__item" onClick={handleLogout}>
              <div className="mobile-drawer-menu__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 7V4C15 3.44772 14.5523 3 14 3H4C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H14C14.5523 21 15 20.5523 15 20V17" stroke="#333333" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 12H23M23 12L20 9M23 12L20 15" stroke="#333333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="mobile-drawer-menu__title">Выйти</div>
            </div>

            <div className="mobile-drawer-socials">
              <a aria-label="TikTok" className="mobile-drawer-socials__item mobile-drawer-socials__item--tiktok" href="/" target="_blank" rel="noopener noreferrer"></a>
              <a aria-label="Instagram" className="mobile-drawer-socials__item mobile-drawer-socials__item--instagram" href="/" target="_blank" rel="noopener noreferrer"></a>
              <div className="mobile-drawer-socials__item mobile-drawer-socials__item--locale">RU</div>
            </div>

            <div
              className="mobile-drawer-create-btn"
              onClick={openMobileCreateDrawer}
            >
              Создать
            </div>
          </div>
        </Drawer>

        <Drawer
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ width: '24px' }}></span>
              <span style={{ fontWeight: '600' }}>Служба поддержки</span>
              <img
                src={closeIcon}
                alt="close"
                onClick={closeMobileSupportDrawer}
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              />
            </div>
          }
          placement="bottom"
          closable={false}
          onClose={closeMobileSupportDrawer}
          open={mobileSupportDrawerOpen}
          height="auto"
          push={false}
          className="mobile-only"
          rootClassName="mobile-drawer-menu-container mobile-support-drawer-container"
        >
          <div className="mobile-drawer-menu mobile-support-drawer">
            {mobileSupportContent}
          </div>
        </Drawer>

        <Drawer
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span style={{ width: '24px' }}></span>
              <span style={{ fontWeight: '600' }}>Создать</span>
              <img
                src={closeIcon}
                alt="close"
                onClick={() => setMobileCreateDrawerOpen(false)}
                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              />
            </div>
          }
          placement="bottom"
          closable={false}
          onClose={() => setMobileCreateDrawerOpen(false)}
          open={mobileCreateDrawerOpen}
          height="auto"
          push={false}
          className="mobile-only"
          rootClassName="mobile-drawer-menu-container mobile-create-drawer-container"
          styles={{
            body: { padding: 0, backgroundColor: '#F0F4F7' }
          }}
        >
          <div className="mobile-create-drawer">
            <button
              type="button"
              className="mobile-create-drawer__item"
              onClick={() => handleMobileCreate('/orders/create')}
            >
              <span className="mobile-create-drawer__item__icon mobile-create-drawer__item__icon--order" />
              <span className="mobile-create-drawer__item__content">
                <span className="mobile-create-drawer__item__title">Создать груз</span>
                <span className="mobile-create-drawer__item__subtitle">Новый запрос на перевозку</span>
              </span>
            </button>

            <button
              type="button"
              className="mobile-create-drawer__item"
              onClick={() => handleMobileCreate('/my-fleet?add=1')}
            >
              <span className="mobile-create-drawer__item__icon mobile-create-drawer__item__icon--transport" />
              <span className="mobile-create-drawer__item__content">
                <span className="mobile-create-drawer__item__title">Создать транспорт</span>
                <span className="mobile-create-drawer__item__subtitle">Новое объявление о транспорте</span>
              </span>
            </button>
          </div>
        </Drawer>

        <div className="home__wrapper">
          <div className={`small-sidebar ${sidebarExpanded ? "expanded" : ""}`}>
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `small-sidebar__item ${isActive ? "active" : ""}`
              }
              onClick={() => {
                handleMobileRouteClick("/", { search: true });
              }}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--search" />
              <div className="small-sidebar__item__title">Главная</div>
            </NavLink>
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `small-sidebar__item ${isActive || location.pathname === '/transports' ? "active" : ""}`
              }
              onClick={() => {
                handleMobileRouteClick("/search", { search: true });
              }}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--search" />
              <div className="small-sidebar__item__title">Поиск</div>
            </NavLink>
            {/* <NavLink to="/order" className={({isActive}) =>
              `small-sidebar__item ${isActive ? 'active' : ''}`
            }>
              <div
                className={`small-sidebar__item__icon small-sidebar__item__icon--create ${selected === 'order' ? 'active' : ''}`}
              />
              <div className="small-sidebar__item__title">
                Создать груз
              </div>
            </div> */}
            <NavLink
              to="/my-orders"
              className={({ isActive }) =>
                `small-sidebar__item ${isActive ? "active" : ""}`
              }
              onClick={() => handleMobileRouteClick("/my-orders")}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--my-orders" />
              <div className="small-sidebar__item__title">Мои грузы</div>
            </NavLink>

            <NavLink
              to="/my-fleet"
              className={({ isActive }) =>
                `small-sidebar__item ${isActive ? "active" : ""}`
              }
              onClick={() => handleMobileRouteClick("/my-fleet")}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--my-ads" />
              <div className="small-sidebar__item__title">Мой транспорт</div>
            </NavLink>
            <NavLink
              to="/companies"
              className={({ isActive }) =>
                `small-sidebar__item mobile-hide ${isActive ? "active" : ""}`
              }
              onClick={() => handleMobileRouteClick("/companies", { companies: true })}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--companies">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 22H4C3.4 22 3 21.6 3 21V7.60004C3 6.20004 4 5.00004 5.3 4.70004L12.7 3.00005C13 2.90005 13.3 3.00004 13.5 3.20004C13.7 3.40004 13.9 3.70005 13.9 4.00005V21C14 21.5 13.6 22 13 22ZM5 20H12V5.20004L5.8 6.60004C5.3 6.70004 5 7.10004 5 7.60004V20Z" fill="currentColor" />
                  <path d="M21 22H13C12.4 22 12 21.6 12 21V6.00005C12 5.70005 12.1 5.40004 12.4 5.20004C12.6 5.00004 13 4.90005 13.3 5.00005L19.8 6.60004C21.1 6.90004 22.1 8.10005 22.1 9.50005V20.9C22 21.5 21.6 22 21 22ZM14 20H20V9.60004C20 9.10004 19.7 8.70004 19.2 8.60004L14 7.30005V20Z" fill="currentColor" />
                  <path d="M18 11H16C15.4 11 15 10.6 15 10C15 9.4 15.4 9 16 9H18C18.6 9 19 9.4 19 10C19 10.6 18.6 11 18 11Z" fill="currentColor" />
                  <path d="M17.9996 14H16.0996C15.4996 14 15.0996 13.6 15.0996 13C15.0996 12.4 15.4996 12 16.0996 12H17.9996C18.5996 12 18.9996 12.4 18.9996 13C18.9996 13.6 18.5996 14 17.9996 14Z" fill="currentColor" />
                  <path d="M18 17H16C15.4 17 15 16.6 15 16C15 15.4 15.4 15 16 15H18C18.6 15 19 15.4 19 16C19 16.6 18.6 17 18 17Z" fill="currentColor" />
                  <path d="M9 22H8C7.4 22 7 21.6 7 21V18C7 17.4 7.4 17 8 17H9C9.6 17 10 17.4 10 18V21C10 21.5 9.6 22 9 22Z" fill="currentColor" />
                  <path d="M10 10C9.7 10 9.5 9.89999 9.3 9.69999C9.1 9.49999 9 9.2 9 9C9 8.7 9.1 8.5 9.3 8.3C9.7 7.9 10.3 7.9 10.7 8.3C10.9 8.5 11 8.7 11 9C11 9.3 10.9 9.49999 10.7 9.69999C10.5 9.89999 10.3 10 10 10Z" fill="currentColor" />
                  <path d="M7 10C6.7 10 6.5 9.89999 6.3 9.69999C6.1 9.49999 6 9.3 6 9C6 8.7 6.1 8.5 6.3 8.3C6.7 7.9 7.3 7.9 7.7 8.3C7.9 8.5 8 8.7 8 9C8 9.3 7.9 9.49999 7.7 9.69999C7.6 9.79999 7.5 9.89999 7.4 9.89999C7.3 9.89999 7.2 10 7 10Z" fill="currentColor" />
                  <path d="M10 13C9.7 13 9.5 12.9 9.3 12.7C9.1 12.5 9 12.3 9 12C9 11.7 9.1 11.5 9.3 11.3C9.7 10.9 10.3 10.9 10.7 11.3C10.9 11.5 11 11.7 11 12C11 12.3 10.9 12.5 10.7 12.7C10.5 12.9 10.3 13 10 13Z" fill="currentColor" />
                  <path d="M7 13C6.7 13 6.5 12.9 6.3 12.7C6.1 12.5 6 12.3 6 12C6 11.7 6.1 11.5 6.3 11.3C6.6 11 7 10.9 7.4 11.1C7.5 11.2 7.6 11.2 7.7 11.3C7.9 11.5 8 11.7 8 12C8 12.1 8 12.1 8 12.2C8 12.3 8 12.3 7.9 12.4C7.9 12.5 7.8 12.5 7.8 12.6C7.8 12.7 7.7 12.7 7.7 12.8C7.6 12.9 7.5 13 7.4 13C7.3 12.9 7.2 13 7 13Z" fill="currentColor" />
                  <path d="M10 16C9.7 16 9.5 15.9 9.3 15.7C9.1 15.5 9 15.3 9 15C9 14.7 9.1 14.5 9.3 14.3C9.7 13.9 10.3 13.9 10.7 14.3C10.9 14.5 11 14.7 11 15C11 15.3 10.9 15.5 10.7 15.7C10.5 15.9 10.3 16 10 16Z" fill="currentColor" />
                </svg>

              </div>
              <div className="small-sidebar__item__title">Компании</div>
            </NavLink>
            <NavLink
              to="/notifications"
              className={({ isActive }) =>
                `small-sidebar__item ${isActive ? "active" : ""}`
              }
              onClick={() => handleMobileRouteClick("/notifications")}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--notifications" />
              <div className="small-sidebar__item__title">Уведомления</div>
              {showNotification && (
                <span className="small-sidebar__item__icon__dot"></span>
              )}
            </NavLink>
            <div
              className={`small-sidebar__item mobile-only ${mobileDrawerOpen ? "active" : ""}`}
              onClick={() => {
                const willOpen = !mobileDrawerOpen;
                if (willOpen) {
                  setMobileRouteDrawerOpen(false);
                  setMobileCreateDrawerOpen(false);
                  setMobileSupportDrawerOpen(false);
                  window.dispatchEvent(new CustomEvent('closeMobileDrawers'));
                }
                setMobileDrawerOpen(willOpen);
              }}
            >
              <div className="small-sidebar__item__icon small-sidebar__item__icon--menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="#272A36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 6H21" stroke="#272A36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 18H21" stroke="#272A36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            <div
              style={{ marginTop: 'auto' }}
              className={`small-sidebar__toggle desktop-only ${sidebarExpanded ? "expanded" : ""}`}
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
            >
              <div className="small-sidebar__toggle__icon" />
              <div className="small-sidebar__toggle__text">Свернуть</div>
            </div>
          </div>
          {show && (
            shouldUseMobileRouteDrawer ? (
              <Drawer
                placement="bottom"
                closable={false}
                title={
                  showMobileRouteDrawerHeader ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span style={{ width: '24px' }}></span>
                      <span style={{ fontWeight: '600' }}>{mobileRouteDrawerTitle}</span>
                      <img
                        src={closeIcon}
                        alt="close"
                        onClick={closeMobileRouteDrawer}
                        style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                      />
                    </div>
                  ) : null
                }
                open={mobileRouteDrawerOpen}
                onClose={closeMobileRouteDrawer}
                height="auto"
                forceRender
                destroyOnClose={false}
                push={false}
                className="mobile-only"
                rootClassName={`hhh mobile-drawer-menu-container mobile-route-drawer-container ${isMobileOtherLevelDrawer ? "mobile-drawer-other-level" : ""} ${normalizedPathname === "/companies" ? "mobile-companies-drawer" : ""}`}
                styles={{
                  header: showMobileRouteDrawerHeader ? {} : { display: 'none' },
                  body: {
                    padding: showMobileRouteDrawerHeader ? 0 : 0,
                    backgroundColor: '#F0F4F7',
                    ...(mobileRouteDrawerConfig.bodyStyle || {}),
                  },
                }}
              >
                <div
                  className="mobile-route-drawer__content"
                  style={{
                    maxHeight: isMobileOtherLevelDrawer ? '100%' : 'calc(100vh - 85px - 80px)',
                    overflowY: 'auto',
                    ...(mobileRouteDrawerConfig.contentStyle || {}),
                  }}
                >
                  {outletContent}
                </div>
              </Drawer>
            ) : (
              <div
                className={`middle-sidebar ${middleSidebarExpanded ? "expanded" : ""}`}
              >
                <div className={`middle-sidebar__wrapper`}>
                  {outletContent}
                </div>
                <div
                  className="middle-sidebar__toggle"
                  onClick={() => setMiddleSidebarExpanded(!middleSidebarExpanded)}
                >
                  <div className="middle-sidebar__toggle__icon" />
                </div>
                <div
                  className="middle-sidebar__close mobile"
                  onClick={() => closeMiddleSidebar(false)}
                >
                  <div className="middle-sidebar__close__icon" />
                </div>
              </div>
            )
          )}
        </div>
        <div className="home-map">
          <Map
            drivers={drivers}
            showOnMap={showOnMap}
            scrollTo={scrollTo}
            selected={location.pathname.split("/")[1] || "search"}
            selectedSearch={selectedSearch}
          />
        </div>
      </div>
    </HomeContext.Provider>
  );
};

export default Home;
